import React, { createContext, useState, useContext, useEffect } from "react";
import { db, doc, getDoc, setDoc, serverTimestamp, getAuthInstance } from "../config/firebase";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe = () => {};

    const setupAuthListener = async () => {
      // firebase/auth SDK loads here, only when the app actually needs it —
      // but this still runs right away on mount, just not blocking initial render.
      const { onAuthStateChanged } = await import("firebase/auth");
      const auth = await getAuthInstance();

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);

          try {
            // Fetch user profile
            const profileDoc = await getDoc(doc(db, "users", firebaseUser.uid));
            if (profileDoc.exists()) {
              setUserProfile(profileDoc.data());
            } else {
              const newProfile = {
                name: firebaseUser.displayName || "",
                email: firebaseUser.email || "",
                phone: firebaseUser.phoneNumber || "",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };
              await setDoc(doc(db, "users", firebaseUser.uid), newProfile);
              setUserProfile(newProfile);
            }
          } catch (err) {
            console.error("Error fetching user profile:", err);
          }

          try {
            // Check if user is admin
            const adminDoc = await getDoc(doc(db, "admins", firebaseUser.uid));
            setIsAdmin(adminDoc.exists());
          } catch (err) {
            console.error("Error checking admin status:", err);
            setIsAdmin(false);
          }

        } else {
          setUser(null);
          setUserProfile(null);
          setIsAdmin(false);
        }
        setLoading(false);
      });
    };

    setupAuthListener();

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    try {
      const { signOut } = await import("firebase/auth");
      const auth = await getAuthInstance();
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      setIsAdmin(false);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, userProfile, setUserProfile, isAdmin, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};